import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { usePostPublicComment, usePublicReview } from "../api/share";
import {
  MAX_AUTHOR_CHARS,
  MAX_COMMENT_CHARS,
  type ShareComment,
} from "../api/types";
import { ReusePanel } from "../ad/ReusePanel";
import { formatDate, formatDuration } from "../ui/format";
import { Button, Card, Field, Input, Skeleton } from "../ui";

export const REVIEWER_NAME_KEY = "truereel.reviewer_name";

/** The reviewer's name, remembered per browser so the second comment does
 *  not ask again. Storage may be absent or refused; then it is just not
 *  remembered. */
export function loadReviewerName(): string {
  try {
    return window.localStorage.getItem(REVIEWER_NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export function saveReviewerName(name: string): void {
  try {
    window.localStorage.setItem(REVIEWER_NAME_KEY, name);
  } catch {
    // Private mode or storage disabled: nothing to do.
  }
}

const UNAVAILABLE = "This link is not available.";
const TOO_MANY = "Too many comments; try again later.";

function CommentList({
  comments,
  onSeek,
}: {
  comments: ShareComment[];
  onSeek: (t: number) => void;
}) {
  if (comments.length === 0) {
    return <p className="m-0 text-sm text-slate-500">No comments yet.</p>;
  }
  return (
    <ul
      className="m-0 flex list-none flex-col gap-3 p-0 text-sm"
      aria-label="Comments"
    >
      {comments.map((c) => (
        <li
          key={c.id}
          className="flex flex-col gap-1"
          data-testid={`comment-${c.id}`}
        >
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-semibold text-slate-800">
              {c.author_name}
            </span>
            {c.t !== null ? (
              <button
                type="button"
                className="tb-pill tb-pill-sm tb-pill-purple cursor-pointer border-0"
                aria-label={`At ${formatDuration(c.t)}`}
                title="Jump to this moment"
                onClick={() => onSeek(c.t ?? 0)}
              >
                {formatDuration(c.t)}
              </button>
            ) : null}
          </div>
          {/* Text nodes only: never markup (review-links.md §2). */}
          <p className="m-0 whitespace-pre-wrap text-slate-700">{c.body}</p>
        </li>
      ))}
    </ul>
  );
}

/** `/r/:token` (docs/15-product/review-links.md): the public review page,
 *  outside the shell. The film, the thread with time codes, and a comment
 *  form when the link allows it; when the link allows reuse (P27-T01), the
 *  panel that takes its scenes and copies its templates for a signed-in
 *  person. Nothing else of the ad is here. */
export function ReviewPage() {
  const { token = "" } = useParams<{ token: string }>();
  const review = usePublicReview(token);
  const post = usePostPublicComment(token);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [name, setName] = useState(loadReviewerName);
  const [body, setBody] = useState("");
  const [atCurrent, setAtCurrent] = useState(true);

  const seek = (t: number) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = t;
  };

  const trimmedName = name.trim();
  const trimmedBody = body.trim();
  const canPost = trimmedName !== "" && trimmedBody !== "" && !post.isPending;

  const submit = () => {
    if (!canPost) return;
    const t =
      atCurrent && videoRef.current
        ? Math.round(videoRef.current.currentTime * 10) / 10
        : null;
    post.mutate(
      { author_name: trimmedName, body: trimmedBody, t },
      {
        onSuccess: () => {
          saveReviewerName(trimmedName);
          setBody("");
        },
      },
    );
  };

  if (review.isPending) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Card aria-busy="true" className="flex flex-col gap-3 p-6">
          <Skeleton height={20} width="50%" />
          <Skeleton height={240} />
        </Card>
      </main>
    );
  }

  if (review.isError) {
    const code = review.error instanceof ApiError ? review.error.code : null;
    return (
      <main className="mx-auto max-w-3xl px-4 py-8">
        <Card role="alert" className="p-6 text-slate-700">
          {code === "not_found" || code === null
            ? UNAVAILABLE
            : "The review page could not be loaded. Please try again."}
        </Card>
      </main>
    );
  }

  const page = review.data;
  const postError =
    post.error instanceof ApiError
      ? post.error.code === "rate_limited"
        ? TOO_MANY
        : post.error.code === "not_found"
          ? UNAVAILABLE
          : post.error.code === "validation_error"
            ? post.error.message
            : "The comment could not be posted. Please try again."
      : post.isError
        ? "The comment could not be posted. Please try again."
        : null;

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-8">
      <header className="flex flex-wrap items-baseline gap-3">
        <span className="gradient-text text-xl font-extrabold tracking-tight">
          TrueReel
        </span>
        <h1 className="tb-h-section m-0">{page.title}</h1>
        <span className="text-xs text-slate-500">
          Review link · expires {formatDate(page.expires_at)}
        </span>
      </header>

      <Card className="flex flex-col gap-4 p-6">
        {page.video_url ? (
          <video
            ref={videoRef}
            src={page.video_url}
            controls
            playsInline
            preload="metadata"
            data-testid="review-video"
            className="mx-auto max-h-[70vh] w-full rounded bg-black"
            style={{
              aspectRatio: (page.aspect ?? "16:9").replace(":", " / "),
            }}
          />
        ) : (
          <p className="m-0 text-sm text-slate-600">No video is ready yet.</p>
        )}
        <CommentList comments={page.comments} onSeek={seek} />
      </Card>

      {page.allow_comments ? (
        <Card className="p-6">
          <form
            className="flex flex-col gap-3"
            aria-label="Leave a comment"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <Field label="Your name">
              <Input
                value={name}
                maxLength={MAX_AUTHOR_CHARS}
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
            <Field
              label="Comment"
              helper={`${body.length.toLocaleString("en-US")} / ${MAX_COMMENT_CHARS.toLocaleString("en-US")}`}
            >
              <textarea
                className="tb-input min-h-[72px] resize-y"
                value={body}
                maxLength={MAX_COMMENT_CHARS}
                onChange={(e) => setBody(e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={atCurrent}
                disabled={!page.video_url}
                onChange={(e) => setAtCurrent(e.target.checked)}
              />
              Pin to the current time of the video
            </label>
            {postError ? (
              <p role="alert" className="m-0 text-sm text-slate-700">
                {postError}
              </p>
            ) : null}
            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={!canPost}
                aria-busy={post.isPending}
              >
                {post.isPending ? "Posting…" : "Post comment"}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <p className="m-0 text-sm text-slate-500">
          Comments are off for this link.
        </p>
      )}
      {page.reuse ? <ReusePanel token={token} /> : null}
    </main>
  );
}
